import { Popover, Transition } from "@headlessui/react";
import clsx from "clsx";
import React, { FC, ReactNode, useRef, useState, Fragment } from "react";
import { usePopper } from "react-popper";

export type StandardPopOverOption = {
  content: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  as?: "button";
};

interface StandardPopoverButtonProps {
  options: StandardPopOverOption[];
  icon: ReactNode;
}

const StandardPopoverButton: FC<StandardPopoverButtonProps> = (props) => {
  const { icon, options } = props;
  const popperElRef = useRef();
  const [referenceElement, setReferenceElement] = useState<any>();
  const [popperElement, setPopperElement] = useState<any>();
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "bottom-end",
    modifiers: [
      {
        name: "offset",
        options: { offset: [0, 8] },
      },
      { name: "preventOverflow" },
    ],
  });

  if (!options || options?.length === 0) return null;

  return (
    <Popover className="relative flex items-center z-40">
      {({ open }) => (
        <>
          <Popover.Button
            ref={setReferenceElement}
            className="outline-none ring-0 p-1"
          >
            {icon}
          </Popover.Button>
          <div ref={popperElRef} style={styles.popper} {...attributes.popper}>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
              beforeEnter={() => setPopperElement(popperElRef.current)}
              afterLeave={() => setPopperElement(null)}
            >
              <Popover.Panel
                ref={setPopperElement}
                className="z-40 max-w-xs md:mac-w-xs bg-white rounded-lg shadow-lg py-2 flex flex-col space-y-3"
              >
                {({ close }) => {
                  return options.map((option, idx) => {
                    const onClick = () => {
                      option.onClick();
                      close();
                    };

                    const className =
                      "text-base text-gray-900  py-2 px-6 cursor-pointer flex items-center transition duration-150 ease-in-out rounded-lg hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-50 whitespace-pre";

                    if (option.as === "button") {
                      return (
                        <button
                          key={idx}
                          className={clsx(
                            className,
                            option.disabled && "disabled",
                          )}
                          disabled={option.disabled}
                          onClick={onClick}
                        >
                          {option.content}
                        </button>
                      );
                    }

                    return (
                      <div key={idx} className={className} onClick={onClick}>
                        <span>{option.content}</span>
                      </div>
                    );
                  });
                }}
              </Popover.Panel>
            </Transition>
          </div>
        </>
      )}
    </Popover>
  );
};

export default StandardPopoverButton;
